import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  getAllJobs,
  searchJobs,
  clearFilteredJobs,
} from "../../store/jobSlice";
import { logoutUser } from "../../store/authSlice";
import { User } from "lucide-react";
import { setPopup } from "../../store/popupSlice";

export default function Navbar() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { countries, cities, jobs } = useSelector((state) => state.job);
  const { user } = useSelector((state) => state.auth);

  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);

  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [cityDropdownOpen, setCityDropdownOpen] = useState(false);

  const [scrollProgress, setScrollProgress] = useState(0);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);

  const profileRef = useRef(null);
  const countryRef = useRef(null);
  const cityRef = useRef(null);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  // 🔹 Load jobs initially
  useEffect(() => {
    dispatch(getAllJobs());
  }, [dispatch]);

  // 🔹 Scroll progress
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const windowHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const scrolled = (scrollTop / windowHeight) * 100;
      setScrollProgress(scrolled);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // 🔹 Close mobile menu on outside click or scroll
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (
        menuRef.current?.contains(e.target) ||
        buttonRef.current?.contains(e.target)
      ) {
        return;
      }
      setIsOpen(false);
    };

    const handleScroll = () => {
      setIsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isOpen]);

  // 🔹 Reset cities when countries change
  useEffect(() => {
    if (selectedCountries.length > 0) {
      setSelectedCities([]);
    }
  }, [selectedCountries]);

  // 🔹 Debounced search + filter
  useEffect(() => {
    const delay = setTimeout(() => {
      if (
        searchValue ||
        selectedCountries.length > 0 ||
        selectedCities.length > 0
      ) {
        dispatch(
          searchJobs({
            query: searchValue,
            countries: selectedCountries,
            cities: selectedCities,
          })
        );
      } else {
        dispatch(clearFilteredJobs());
      }
      scrollToMoreJobs();
    }, 400);

    return () => clearTimeout(delay);
  }, [searchValue, selectedCountries, selectedCities, dispatch]);

  const scrollToMoreJobs = () => {
    window.dispatchEvent(new CustomEvent("scrollToMoreJobs"));
  };

  // 🔹 Close dropdowns on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (isOpen) return;
      if (
        countryRef.current?.contains(e.target) ||
        cityRef.current?.contains(e.target) ||
        profileRef.current?.contains(e.target)
      )
        return;
      setCountryDropdownOpen(false);
      setCityDropdownOpen(false);
      setProfileDropdownOpen(false);
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleLogout = () => {
    dispatch(logoutUser());
    navigate("/");
    setIsOpen(false);
  };

  const toggleSelection = (value, selected, setSelected) => {
    if (selected.includes(value)) {
      setSelected(selected.filter((v) => v !== value));
    } else {
      setSelected([...selected, value]);
    }
  };

  // ✅ Filter cities dynamically
  const availableCities =
    selectedCountries.length > 0
      ? cities.filter((city) =>
          jobs.some(
            (job) =>
              selectedCountries.includes(job.Country) && job.City === city
          )
        )
      : cities;

  return (
    <nav className="fixed top-0 w-full z-50 bg-gradient-to-r from-indigo-600 to-teal-500 shadow-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-20 items-center">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link
              to="/"
              className="flex items-center gap-2 text-2xl font-bold text-white hover:opacity-90 transition"
            >
              <img
                src="/JobIntelLogo.png"
                className="h-20 w-20 object-contain"
                alt="JobIntel Logo"
              />
              JobIntelPro
            </Link>
          </div>

          {/* Desktop menu */}
          <div className="hidden md:flex items-center gap-4">
            {/* 🔍 Search */}
            <input
              type="text"
              placeholder="Search jobs..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-yellow-300"
            />

            {/* 🌍 Country filter */}
            <div className="relative" ref={countryRef}>
              <button
                onClick={() => {
                  setCountryDropdownOpen(!countryDropdownOpen);
                  if (!countryDropdownOpen) setCityDropdownOpen(false);
                }}
                className="px-3 py-2 rounded-lg bg-white text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-300 transition"
              >
                Jobs by Countries
              </button>
              {countryDropdownOpen && (
                <div className="absolute left-0 mt-2 w-48 bg-white shadow-lg rounded-lg p-2 z-50 max-h-48 overflow-y-auto">
                  {selectedCountries.length > 0 && (
                    <button
                      onClick={() => {
                        setSelectedCountries([]);
                        dispatch(clearFilteredJobs());
                      }}
                      className="w-full text-left px-2 py-1 mb-2 text-red-500 hover:bg-gray-100 rounded"
                    >
                      Clear All
                    </button>
                  )}
                  {countries.map((country, idx) => (
                    <label
                      key={`${country}-${idx}`}
                      className="flex items-center space-x-2 px-2 py-1 hover:bg-gray-100 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedCountries.includes(country)}
                        onChange={() =>
                          toggleSelection(
                            country,
                            selectedCountries,
                            setSelectedCountries
                          )
                        }
                      />
                      <span>{country}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* 🏙 City filter */}
            <div className="relative" ref={cityRef}>
              <button
                onClick={() => {
                  setCityDropdownOpen(!cityDropdownOpen);
                  if (!cityDropdownOpen) setCountryDropdownOpen(false);
                }}
                className="px-3 py-2 rounded-lg bg-white text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-300 transition"
              >
                Jobs by Cities
              </button>
              {cityDropdownOpen && (
                <div className="absolute left-0 mt-2 w-48 bg-white shadow-lg rounded-lg p-2 z-50 max-h-48 overflow-y-auto">
                  {selectedCities.length > 0 && (
                    <button
                      onClick={() => {
                        setSelectedCities([]);
                        dispatch(clearFilteredJobs());
                      }}
                      className="w-full text-left px-2 py-1 mb-2 text-red-500 hover:bg-gray-100 rounded"
                    >
                      Clear All
                    </button>
                  )}
                  {availableCities.map((city, idx) => (
                    <label
                      key={`${city}-${idx}`}
                      className="flex items-center space-x-2 px-2 py-1 hover:bg-gray-100 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedCities.includes(city)}
                        onChange={() =>
                          toggleSelection(
                            city,
                            selectedCities,
                            setSelectedCities
                          )
                        }
                      />
                      <span>{city}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <Link
              to="/internships"
              className="px-3 py-2 rounded-lg bg-white text-gray-700 shadow-sm 
             focus:outline-none focus:ring-2 focus:ring-yellow-300 transition hover:bg-gray-100"
            >
              Internships
            </Link>
          </div>

          <div className="hidden md:flex gap-4">
            {!user && (
              <>
                <button
                  onClick={() => navigate("/login")}
                  className="px-4 py-1 border border-white rounded-md text-white hover:bg-white hover:text-indigo-600 transition"
                >
                  Login
                </button>
                <button
                  onClick={() => navigate("/signup")}
                  className="px-4 py-1 border border-white rounded-md text-white hover:bg-white hover:text-indigo-600 transition"
                >
                  Signup
                </button>
              </>
            )}
          </div>

          {/* Desktop Profile Dropdown */}
          <div className="hidden md:block relative ml-4" ref={profileRef}>
            <button
              // ref={buttonRef}
              onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
              className="flex items-center gap-2 px-3 py-2 bg-white text-gray-700 rounded-full shadow"
            >
              <span className="text-xl">☰</span>
              <div className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold">
                {user ? user.name?.[0]?.toUpperCase() : <User size={18} />}
              </div>
            </button>

            {profileDropdownOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white shadow-lg rounded-lg p-2 z-50">
                <button
                  onClick={() => {
                    // if (user) {
                    //   navigate("/hackathons");
                    // } else {
                    //   dispatch(
                    //     setPopup({
                    //       message: "Please login to view Hackathon page",
                    //     })
                    //   );
                    //   navigate("/login", { state: { from: "/hackathons" } });
                    // }
                    navigate("/hackathons");
                    setProfileDropdownOpen(false);
                  }}
                  className="inline-block px-3 py-2 text-gray-700 hover:bg-gray-100 w-full rounded"
                >
                  Hackathon
                </button>

                <button
                  onClick={() => {
                    navigate("/blog");

                    setProfileDropdownOpen(false);
                  }}
                  className="inline-block px-3 py-2 text-gray-700 hover:bg-gray-100 w-full rounded"
                >
                  Blogs
                </button>

                <button
                  onClick={() => {
                    if (user) {
                      navigate("/assessments");
                    } else {
                      dispatch(
                        setPopup({
                          message: "Please login to view Assessment page",
                        })
                      );
                      navigate("/login", { state: { from: "/assessments" } });
                    }
                    setProfileDropdownOpen(false);
                  }}
                  className="inline-block px-3 py-2 text-gray-700 hover:bg-gray-100 w-full rounded"
                >
                  SVAR Assessment
                </button>

                {user && (
                  <button
                    onClick={handleLogout}
                    className="inline-block px-3 py-2 text-red-700 hover:bg-red-100 w-full rounded"
                  >
                    Logout
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Mobile toggle */}
          <div className="md:hidden">
            <button
              ref={buttonRef}
              onClick={() => setIsOpen(!isOpen)}
              className="flex items-center gap-2 px-3 py-2 bg-white text-gray-700 rounded-full shadow"
            >
              <span className="text-xl">☰</span>
              <div className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold">
                {user ? user.name?.[0]?.toUpperCase() : <User size={18} />}
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* 🔹 Scroll Progress Bar */}
      <div className="h-1 bg-gray-200 w-full">
        <div
          className="h-1/2 bg-blue-400 transition-all duration-150"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      {/* ✅ Mobile menu */}
      {isOpen && (
        <div
          ref={menuRef}
          className="md:hidden bg-white shadow-lg px-4 pt-4 pb-6 space-y-4"
        >
          {/* ✅ Mobile Search */}
          <input
            type="text"
            placeholder="Search jobs..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-400"
          />

          <div className="space-y-2">
            <button
              onClick={() => setCountryDropdownOpen(!countryDropdownOpen)}
              className="w-full text-left px-3 py-2 rounded-lg bg-gray-100 text-gray-700"
            >
              Jobs by Countries
            </button>
            {countryDropdownOpen && (
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {countries.map((country, idx) => (
                  <label
                    key={`${country}-${idx}`}
                    className="flex items-center gap-2 px-2 py-1"
                  >
                    <input
                      type="checkbox"
                      checked={selectedCountries.includes(country)}
                      onChange={() =>
                        toggleSelection(
                          country,
                          selectedCountries,
                          setSelectedCountries
                        )
                      }
                    />
                    {country}
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* 🏙 Cities */}
          <div className="space-y-2">
            <button
              onClick={() => setCityDropdownOpen(!cityDropdownOpen)}
              className="w-full text-left px-3 py-2 rounded-lg bg-gray-100 text-gray-700"
            >
              Jobs by Cities
            </button>
            {cityDropdownOpen && (
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {availableCities.map((city, idx) => (
                  <label
                    key={`${city}-${idx}`}
                    className="flex items-center gap-2 px-2 py-1"
                  >
                    <input
                      type="checkbox"
                      checked={selectedCities.includes(city)}
                      onChange={() =>
                        toggleSelection(city, selectedCities, setSelectedCities)
                      }
                    />
                    {city}
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* ✅ Extra links */}
          <Link
            onClick={() => setIsOpen(false)}
            className="block px-3 py-2 rounded-md text-indigo-600 hover:bg-gray-100"
            to="/internships"
          >
            Internships
          </Link>
          <button
            onClick={() => {
              // if (user) {
              //   navigate("/hackathons");
              // } else {
              //   dispatch(
              //     setPopup({ message: "Please login to view Hackathon page" })
              //   );
              //   navigate("/login", { state: { from: "/hackathons" } });
              // }
              navigate("/hackathons");
              setProfileDropdownOpen(false);
            }}
            className="w-full px-3 py-2 rounded-md text-indigo-600 hover:bg-gray-100"
          >
            Hackathon
          </button>
          <button
            onClick={() => {
              navigate("/blog");

              setProfileDropdownOpen(false);
            }}
            className="w-full px-3 py-2 rounded-md text-indigo-600 hover:bg-gray-100"
          >
            Blogs
          </button>

          <button
            onClick={() => {
              if (user) {
                navigate("/assessments");
              } else {
                dispatch(
                  setPopup({ message: "Please login to view Assessment page" })
                );
                navigate("/login", { state: { from: "/assessments" } });
              }
              setProfileDropdownOpen(false);
            }}
            className="w-full px-3 py-2 rounded-md text-indigo-600 hover:bg-gray-100"
          >
            SVAR Assessment
          </button>

          {/* ✅ Auth buttons */}
          <div className="space-y-2">
            {user ? (
              <button
                onClick={handleLogout}
                className="w-full px-4 py-2 border border-red-600 rounded-md text-red-600 hover:bg-red-600 hover:text-white transition"
              >
                Logout
              </button>
            ) : (
              <>
                <button
                  onClick={() => {
                    navigate("/login");
                    setIsOpen(false);
                  }}
                  className="w-full px-4 py-2 border border-indigo-600 rounded-md text-indigo-600 hover:bg-indigo-600 hover:text-white transition"
                >
                  Login
                </button>
                <button
                  onClick={() => {
                    navigate("/signup");
                    setIsOpen(false);
                  }}
                  className="w-full px-4 py-2 border border-indigo-600 rounded-md text-indigo-600 hover:bg-indigo-600 hover:text-white transition"
                >
                  Signup
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
